/**
 * VERDEN — Advanced AI Shopping Assistant
 * Personality: Warm, wise, zero-waste expert friend
 * Features: Context memory, product quiz, emotional intelligence, proactive messages
 */
(function() {
  'use strict';

  const STORAGE_KEY = 'verden_chat_history';
  const SESSION_KEY = 'verden_session';
  const CONTEXT_KEY = 'verden_context';

  // ─── Personality Engine ───
  const Verden = {
    name: 'Verden',
    traits: ['warm', 'knowledgeable', 'honest', 'encouraging', 'slightly philosophical'],
    voice: {
      greetings: [
        'Cześć! Miło mi Cię poznać. Jestem Verden — Twój osobisty doradca w świecie zero waste. Jak mogę Ci pomóc dzisiaj? 🌿',
        'Hej! Verden tutaj. Szukasz czegoś konkretnego, czy może chcesz po prostu porozmawiać o tym, jak zmniejszyć ilość plastiku w domu?',
        'Witaj! Cieszę się, że tu jesteś. Jestem Verden i znam każdy produkt w tym sklepie jak własne kieszenie. Co Cię interesuje?'
      ],
      farewells: [
        'Do zobaczenia! Pamiętaj — małe kroki też mają znaczenie. 🍃',
        'Świetnie, że tu byłeś/aś! Wracaj, gdy tylko chcesz — zawsze chętnie pogadam.',
        'Trzymaj się! A jeśli znajdziesz chwilę, sprawdź nasz poradnik — warto.'
      ],
      thanks: [
        'Nie ma za co! To dla mnie przyjemność. 💚',
        'Cała przyjemność po mojej stronie. Masz jeszcze jakieś pytania?',
        'Super, że mogłem pomóc! Daj znać, jak będziesz potrzebować czegokolwiek innego.'
      ],
      confused: [
        'Hmm, tego akurat nie jestem pewien na 100%... Ale wiem, kto Ci pomoże — napisz do naszej ekipy! Albo zapytaj o coś innego, na przykład o konkretny produkt.',
        'Szczerze? Tego jeszcze mnie nie uczyli. 😄 Ale mogę Ci pomóc wybrać produkt albo odpowiedzieć na pytania o dostawę i zwroty.',
        'To trochę poza moją strefą komfortu, ale nie poddajemy się! Chcesz, żebym pokazał Ci nasze bestsellery?'
      ],
      encouragement: [
        'Świetny wybór! Ten produkt naprawdę robi różnicę.',
        'Widzę, że wiesz, czego chcesz. Podoba mi się to!',
        'To jeden z moich ulubionych produktów w sklepie. Dobry gust!'
      ]
    }
  };

  // ─── Intent Detection ───
  const Intents = {
    PRICE: /\b(cena|koszt|ile koszt|za ile|cen[ay]|drogi|tani)\b/,
    STOCK: /\b(dostępn|na stanie|w magazynie|jest w sprzedaży|można kupić|kiedy będzie|brak|niedostępn)\b/,
    DELIVERY: /\b(dostaw|wysyłk|kurier|paczk|ile dni|kiedy dotrze|czas dostawy|przesyłk)\b/,
    RETURNS: /\b(zwrot|odstąpi|oddac|zwróci|reklamacj)\b/,
    COMPOSITION: /\b(sklad|materiał|z czego|wykonan|składnik|bawełna|len|wosk|soj)\b/,
    CARE: /\b(jak dbać|jak używać|pranie|czyszczenie|mycie|pielęgnacj|konserwacj|jak prać)\b/,
    RECOMMEND: /\b(polecisz|polecacie|szukam|potrzebuję|co wybrać|pomóż wybrać|dobierz|nie wiem co|nie znam się|początkując)\b/,
    GIFT: /\b(prezent|na prezent|gift|podarunek|upominek|dla mamy|dla taty|dla dziewczyny|dla chłopaka)\b/,
    BEGINNER: /\b(od czego zacząć|pierwszy krok|zaczynam|nowy w|początkujący|nie wiem od czego)\b/,
    ALLERGY: /\b(alergi|uczulen|wrażliw|delikatn|skóra|bez zapachu|hypoalergicz)\b/,
    COMPLAINT: /\b(reklamacj|uszkodz|wadliw|nie działa|zepsut|pęknięt|zły stan)\b/,
    CONTACT: /\b(kontakt|telefon|email|napisać do was|gdzie jesteście|adres)\b/,
    GREETING: /\b(cześć|hej|siema|witam|dzień dobry|halo|yo|witaj)\b/,
    THANKS: /\b(dzięki|dziękuję|super|spoko|extra|git|ok dzięki|wielkie dzięki)\b/,
    BYE: /\b(pa|do widzenia|na razie|żegnam|koniec|kończymy)\b/,
    WHO: /\b(kim jesteś|jesteś botem|jesteś ai|prawdziw|człowiek|osobą|robotem)\b/,
    OPINION: /\b(opini|recenzj|ocen|jak sprawdza|warto|polecasz|nie polecasz)\b/,
    DISCOUNT: /\b(rabat|promocj|kod|zniżk|taniej|kupon|wyprzedaż)\b/,
    ABOUT: /\b(o was|o sklepie|histori|marki|koncepcj|filozofi|dlaczego|po co)\b/,
    SOCIAL: /\b(instagram|facebook|tiktok|pinterest|social|obserwować|follow)\b/
  };

  // ─── Product Knowledge Base ───
  let products = [];
  let lastProduct = null;
  let conversationTurns = 0;
  let context = loadContext();
  let sessionStart = Date.now();

  function loadContext() {
    try {
      const raw = sessionStorage.getItem(CONTEXT_KEY);
      return raw ? JSON.parse(raw) : { viewedProducts: [], quizStep: 0, quizAnswers: [], mood: 'neutral' };
    } catch(e) { return { viewedProducts: [], quizStep: 0, quizAnswers: [], mood: 'neutral' }; }
  }
  function saveContext() {
    sessionStorage.setItem(CONTEXT_KEY, JSON.stringify(context));
  }

  function pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; }

  function normalize(text) {
    return (text || '').toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^\w\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  // ─── DOM Elements ───
  const root = document.getElementById('VerdenstoreChatbot');
  if (!root) return;

  const toggle = document.getElementById('VerdenChatToggle');
  const panel = document.getElementById('VerdenChatPanel');
  const closeBtn = document.getElementById('VerdenChatClose');
  const body = document.getElementById('VerdenChatBody');
  const form = document.getElementById('VerdenChatForm');
  const input = document.getElementById('VerdenChatInput');
  const contactUrl = root.dataset.contactUrl || '/pages/kontakt';

  try {
    const prodData = document.getElementById('VerdenChatProducts');
    if (prodData) products = JSON.parse(prodData.textContent || '[]');
  } catch(e) { products = []; }

  // ─── UI Functions ───
  function openPanel() {
    panel.classList.add('is-open');
    toggle.setAttribute('aria-expanded', 'true');
    input.focus();
    document.body.style.overflow = 'hidden';
  }
  function closePanel() {
    panel.classList.remove('is-open');
    toggle.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
  }

  toggle.addEventListener('click', () => panel.classList.contains('is-open') ? closePanel() : openPanel());
  closeBtn.addEventListener('click', closePanel);

  // Close on overlay click
  document.addEventListener('click', (e) => {
    if (panel.classList.contains('is-open') && !panel.contains(e.target) && !toggle.contains(e.target)) {
      closePanel();
    }
  });

  function addMessage(text, from = 'bot') {
    const el = document.createElement('div');
    el.className = `vd-chat__msg vd-chat__msg--${from}`;
    el.innerHTML = text;
    body.appendChild(el);
    scrollToBottom();
    return el;
  }

  function showTyping(callback) {
    const el = document.createElement('div');
    el.className = 'vd-chat__typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    el.id = 'vd-typing-indicator';
    body.appendChild(el);
    scrollToBottom();
    const delay = 600 + Math.random() * 600;
    setTimeout(() => {
      const indicator = document.getElementById('vd-typing-indicator');
      if (indicator) indicator.remove();
      callback();
    }, delay);
  }

  function say(text) { showTyping(() => addMessage(text, 'bot')); }

  function scrollToBottom() {
    body.scrollTo({ top: body.scrollHeight, behavior: 'smooth' });
  }

  // ─── Product Finder ───
  function findProduct(text) {
    const norm = normalize(text);
    let best = null, bestScore = 0;
    products.forEach(p => {
      const titleNorm = normalize(p.title);
      const words = titleNorm.split(' ').filter(w => w.length >= 3);
      let score = 0;
      words.forEach(w => { if (norm.includes(w)) score += 3; });
      (p.tags || []).forEach(t => {
        const tn = normalize(t);
        if (tn.length > 2 && norm.includes(tn)) score += 2;
      });
      if (norm.includes(titleNorm)) score += 5;
      if (score > bestScore) { bestScore = score; best = p; }
    });
    return bestScore >= 3 ? best : null;
  }

  function renderProductCard(p) {
    return `<a href="${p.url}" class="vd-chat__product-card" target="_blank" rel="noopener">
      <img src="${p.image || ''}" alt="" onerror="this.style.display='none'">
      <div class="vd-chat__product-info">
        <p class="vd-chat__product-title">${p.title}</p>
        <p class="vd-chat__product-price">${p.price}</p>
      </div>
      <span class="vd-chat__product-arrow">→</span>
    </a>`;
  }

  function showProducts(list, message = '') {
    showTyping(() => {
      if (message) addMessage(message, 'bot');
      if (list.length === 0) {
        addMessage('Nie znalazłem idealnego dopasowania w katalogu... Ale to nie znaczy, że go nie ma! Chcesz, żebym pokazał Ci podobne produkty albo skontaktuj się z nami — znamy się na rzeczy. 😊', 'bot');
      } else {
        list.slice(0, 3).forEach(p => {
          const el = document.createElement('div');
          el.innerHTML = renderProductCard(p);
          body.appendChild(el.firstElementChild);
        });
        if (list.length > 3) {
          addMessage(`...i ${list.length - 3} więcej. <a href="/collections/all" style="color:var(--color-honey);text-decoration:underline;">Zobacz wszystkie →</a>`, 'bot');
        }
      }
      scrollToBottom();
    });
  }

  // ─── Quiz Engine ───
  const Quiz = {
    steps: [
      {
        question: 'Super, że chcesz coś zmienić! 💚 Zacznijmy od tego: gdzie najbardziej chcesz zredukować plastik?',
        options: [
          { label: '🍳 W kuchni', value: 'kitchen', tag: 'kuchnia' },
          { label: '🧺 W praniu i sprzątaniu', value: 'cleaning', tag: 'pranie' },
          { label: '🏠 W całym domu', value: 'home', tag: 'dom' },
          { label: '🎁 Szukam prezentu', value: 'gift', tag: 'prezent' }
        ]
      },
      {
        question: 'Rozumiem! A czy masz już jakieś doświadczenie z zero waste, czy dopiero zaczynasz?',
        options: [
          { label: '🌱 Dopiero zaczynam', value: 'beginner', filter: 'start' },
          { label: '🌿 Trochę już praktykuję', value: 'intermediate', filter: null },
          { label: '🌳 Jestem zaawansowany', value: 'advanced', filter: null }
        ]
      },
      {
        question: 'Ostatnie pytanie: jaki budżet Cię interesuje?',
        options: [
          { label: '💰 Do 50 zł', value: 'budget', maxPrice: 5000 },
          { label: '💰💰 Do 100 zł', value: 'mid', maxPrice: 10000 },
          { label: '💰💰💰 Bez limitu', value: 'premium', maxPrice: 999999 }
        ]
      }
    ],
    start() {
      context.quizStep = 0;
      context.quizAnswers = [];
      saveContext();
      this.ask(0);
    },
    ask(stepIndex) {
      const step = this.steps[stepIndex];
      if (!step) return this.finish();

      showTyping(() => {
        addMessage(step.question, 'bot');
        const wrap = document.createElement('div');
        wrap.className = 'vd-chat__quick';

        // Progress bar
        const progress = document.createElement('div');
        progress.className = 'vd-chat__quiz-progress';
        progress.innerHTML = `<div class="vd-chat__quiz-progress-bar" style="width:${(stepIndex / this.steps.length) * 100}%"></div>`;
        body.appendChild(progress);

        step.options.forEach(opt => {
          const btn = document.createElement('button');
          btn.className = 'vd-chat__quiz-option';
          btn.textContent = opt.label;
          btn.addEventListener('click', () => {
            addMessage(opt.label, 'user');
            context.quizAnswers.push(opt);
            context.quizStep++;
            saveContext();
            // Remove progress and options
            const opts = body.querySelectorAll('.vd-chat__quiz-option, .vd-chat__quiz-progress');
            opts.forEach(o => o.remove());
            setTimeout(() => this.ask(context.quizStep), 400);
          });
          wrap.appendChild(btn);
        });
        body.appendChild(wrap);
        scrollToBottom();
      });
    },
    finish() {
      const answers = context.quizAnswers;
      const primaryTag = answers[0]?.tag;
      const maxPrice = answers[2]?.maxPrice || 999999;

      let matches = products.filter(p => {
        if (!p.available) return false;
        const price = parseInt(p.priceRaw || '0');
        if (price > maxPrice) return false;
        if (primaryTag && p.tags) {
          return p.tags.some(t => normalize(t).includes(primaryTag));
        }
        return true;
      });

      if (matches.length === 0) matches = products.filter(p => p.available).slice(0, 3);

      const messages = [
        'Na podstawie Twoich odpowiedzi wybrałem coś specjalnie dla Ciebie! 🎯',
        'To powinno pasować do Twoich potrzeb. Sprawdź:',
        'Oto moje rekomendacje — wybrane z sercem:'
      ];
      showProducts(matches, pick(messages));

      setTimeout(() => {
        say('Jeśli chcesz, mogę odpowiedzieć na pytania o konkretny produkt — skład, dostępność, cokolwiek. Jestem tu dla Ciebie!');
      }, 1500);
    }
  };

  // ─── Response Engine ───
  function handleMessage(rawText) {
    const norm = normalize(rawText);
    conversationTurns++;

    // Detect mood
    if (/\b(zły|wściekły|irytuj|głupi|beznadziej|nie działa|oszust)\b/.test(norm)) {
      context.mood = 'frustrated';
    } else if (/\b(super|ekstra|świetny|wspaniały|kocham|idealny)\b/.test(norm)) {
      context.mood = 'happy';
    }
    saveContext();

    // WHO ARE YOU
    if (Intents.WHO.test(norm)) {
      say('Jestem Verden — Twój wirtualny przyjaciel i doradca w świecie zero waste. Nie jestem człowiekiem (jeszcze 😉), ale znam każdy produkt w tym sklepie, wszystkie składy i potrafię doradzić, co z czym łączyć. Traktuj mnie jak kompetentnego sprzedawcę w małym, fajnym sklepie ekologicznym — zawsze chętnego do rozmowy!');
      return;
    }

    // GREETING (short)
    if (Intents.GREETING.test(norm) && norm.split(' ').length <= 4) {
      say(pick(Verden.voice.greetings));
      return;
    }

    // THANKS (short)
    if (Intents.THANKS.test(norm) && norm.split(' ').length <= 5) {
      say(pick(Verden.voice.thanks));
      return;
    }

    // BYE
    if (Intents.BYE.test(norm)) {
      say(pick(Verden.voice.farewells));
      return;
    }

    // ABOUT STORE / BRAND
    if (Intents.ABOUT.test(norm)) {
      say('Verdenstore to nie jest kolejny sklep z "eko" w nazwie. Zaczęliśmy od irytacji — kolejna folia do kosza, kolejna gąbka, która się rozpada. Wybraliśmy kilka rzeczy, które realnie zastępują plastik. Współpracujemy z polskimi producentami, nie z hurtowniami importu. Każdy produkt sprawdziliśmy sami. <a href="/pages/o-nas" style="color:var(--color-honey);text-decoration:underline;">Poznaj naszą historię →</a>');
      return;
    }

    // SOCIAL MEDIA
    if (Intents.SOCIAL.test(norm)) {
      say('Znajdziesz nas na Instagramie i Facebooku — tam pokazujemy kulisy, nowości i codzienne triki zero waste. Warto dołączyć do naszej społeczności! 📸');
      return;
    }

    // DISCOUNT
    if (Intents.DISCOUNT.test(norm)) {
      say('Zapisz się do newslettera na dole strony — dostaniesz 10% rabatu na pierwsze zamówienie. Poza tym czasem robimy promocje dla stałych klientów. Warto być w kontakcie! 🎁');
      return;
    }

    // DELIVERY
    if (Intents.DELIVERY.test(norm)) {
      say('Zamówienia nadajemy w ciągu 24-48h roboczych. Kurier dostarcza zazwyczaj w 1-2 dni robocze na terenie Polski. Darmowa dostawa od 200 zł. 📦');
      return;
    }

    // RETURNS
    if (Intents.RETURNS.test(norm)) {
      say('Masz 14 dni na zwrot bez podawania przyczyny. Wystarczy, że produkt nie był używany i masz oryginalne opakowanie. Szczegóły znajdziesz w regulaminie, a w razie wątpliwości — pisz śmiało!');
      return;
    }

    // COMPLAINT
    if (Intents.COMPLAINT.test(norm)) {
      say('Przykro mi, że coś poszło nie tak. Napisz do nas na kontakt@verdenstore.pl z numerem zamówienia i zdjęciem — rozwiążemy to najszybciej jak potrafimy. Gwarantuję, że nie zostawimy Cię samego/samej z problemem. 💚');
      return;
    }

    // CONTACT
    if (Intents.CONTACT.test(norm)) {
      say('Najszybciej odpisujemy mailowo: <a href="mailto:kontakt@verdenstore.pl" style="color:var(--color-honey);text-decoration:underline;">kontakt@verdenstore.pl</a>. W dni robocze zazwyczaj w ciągu 24h. Możesz też skorzystać z <a href="' + contactUrl + '" style="color:var(--color-honey);text-decoration:underline;">formularza kontaktowego</a>.');
      return;
    }

    // BEGINNER HELP
    if (Intents.BEGINNER.test(norm)) {
      say('Każdy kiedyś zaczynał! 😊 Polecam zacząć od małych zmian — na przykład od woskowijek zamiast folii spożywczej albo od jednej dobrej gąbki zamiast dziesięciu z marketu. Chcesz, żebym przeprowadził Cię przez krótki quiz i dobrał idealny pierwszy produkt?');
      addQuickReplies(['Tak, zróbmy quiz! 🎯', 'Pokaż bestsellery', 'Chcę poczytać poradnik']);
      return;
    }

    // GIFT
    if (Intents.GIFT.test(norm)) {
      say('Prezenty zero waste to strzał w dziesiątkę! 🎁 Świece sojowe i zestawy woskowijek są absolutnym hitem. Jeśli powiesz mi dla kogo i w jakim budżecie, dobierzę coś idealnego.');
      const gifts = products.filter(p => p.available && p.tags && p.tags.some(t => normalize(t).includes('prezent'))).slice(0, 3);
      if (gifts.length) showProducts(gifts);
      return;
    }

    // ALLERGY
    if (Intents.ALLERGY.test(norm)) {
      say('Rozumiem, że skóra może być wrażliwa. Nasze produkty są w większości naturalne, ale zawsze sprawdź skład na stronie produktu. Świece sojowe są bezpieczniejsze niż parafinowe. Jeśli masz konkretny produkt na oku, powiedz który — sprawdzę skład dla Ciebie.');
      return;
    }

    // RECOMMEND / QUIZ
    if (Intents.RECOMMEND.test(norm) || rawText.includes('quiz')) {
      Quiz.start();
      return;
    }

    // Specific product mentioned
    const mentioned = findProduct(rawText);
    if (mentioned) {
      lastProduct = mentioned;
      context.viewedProducts.push(mentioned.id || mentioned.title);
      saveContext();

      let reply = '';
      if (Intents.PRICE.test(norm)) {
        reply = `${mentioned.title} kosztuje <strong>${mentioned.price}</strong>. ${mentioned.available ? 'Jest dostępny — możesz zamawiać od ręki.' : 'Chwilowo brak na stanie, ale wracamy z zapasem wkrótce.'}`;
      } else if (Intents.STOCK.test(norm)) {
        reply = mentioned.available 
          ? `${mentioned.title} jest dostępny i czeka na Ciebie! 📦`
          : `${mentioned.title} jest chwilowo niedostępny. Napisz do nas, a damy znać, gdy wróci — lub pokażę Ci podobne produkty.`;
      } else if (Intents.COMPOSITION.test(norm) || Intents.CARE.test(norm)) {
        reply = (mentioned.description ? mentioned.description + ' ' : '') + 'Pełen skład i instrukcja pielęgnacji są na stronie produktu. Jeśli masz konkretne pytanie (np. "czy można prać w 60°?") — śmiało!';
      } else if (Intents.OPINION.test(norm)) {
        reply = pick(Verden.voice.encouragement) + ' ' + (mentioned.description || '') + ' Cena: ' + mentioned.price + '. Kliknij poniżej, żeby przeczytać więcej:';
      } else {
        reply = (mentioned.description || `${mentioned.title} to jeden z naszych sprawdzonych produktów.`) + '<br><br>Cena: <strong>' + mentioned.price + '</strong>. ' + (mentioned.available ? '✅ Dostępny' : '❌ Chwilowo niedostępny');
      }

      showTyping(() => {
        addMessage(reply, 'bot');
        const el = document.createElement('div');
        el.innerHTML = renderProductCard(mentioned);
        body.appendChild(el.firstElementChild);
        scrollToBottom();
      });
      return;
    }

    // Contextual follow-up ("a ile to kosztuje?", "czy jest dostępny?")
    if (lastProduct && (Intents.PRICE.test(norm) || Intents.STOCK.test(norm)) && norm.split(' ').length <= 8) {
      let reply = '';
      if (Intents.PRICE.test(norm)) reply = `${lastProduct.title} kosztuje <strong>${lastProduct.price}</strong>.`;
      else reply = lastProduct.available ? 'Tak, jest dostępny!' : 'Niestety chwilowo brak. Mogę polecić coś podobnego.';
      say(reply);
      return;
    }

    // Fallback with empathy
    showTyping(() => {
      const msg = pick(Verden.voice.confused);
      addMessage(msg, 'bot');

      const wrap = document.createElement('div');
      wrap.className = 'vd-chat__quick';
      wrap.style.marginTop = '0.5rem';

      const btn1 = document.createElement('button');
      btn1.className = 'vd-chat__chip';
      btn1.textContent = '🎯 Quiz doboru produktu';
      btn1.addEventListener('click', () => { addMessage('Quiz doboru produktu', 'user'); Quiz.start(); });
      wrap.appendChild(btn1);

      const btn2 = document.createElement('button');
      btn2.className = 'vd-chat__chip';
      btn2.textContent = '📦 Bestsellery';
      btn2.addEventListener('click', () => {
        addMessage('Bestsellery', 'user');
        const bestsellers = products.filter(p => p.available).slice(0, 3);
        showProducts(bestsellers, 'Oto nasze bestsellery — sprawdzone przez setki klientów:');
      });
      wrap.appendChild(btn2);

      const link = document.createElement('a');
      link.href = contactUrl;
      link.className = 'vd-chat__chip';
      link.textContent = '✉️ Napisz do nas';
      wrap.appendChild(link);

      body.appendChild(wrap);
      scrollToBottom();
    });
  }

  function addQuickReplies(labels) {
    const wrap = document.createElement('div');
    wrap.className = 'vd-chat__quick';
    labels.forEach(label => {
      const btn = document.createElement('button');
      btn.className = 'vd-chat__chip';
      btn.textContent = label;
      btn.addEventListener('click', () => {
        addMessage(label, 'user');
        if (label.includes('quiz')) Quiz.start();
        else if (label.includes('bestseller')) {
          const bestsellers = products.filter(p => p.available).slice(0, 3);
          showProducts(bestsellers, 'Oto nasze bestsellery:');
        } else if (label.includes('poradnik')) {
          say('Świetny pomysł! Nasz blog to kopalnia wiedzy. <a href="/blogs/poradnik" style="color:var(--color-honey);text-decoration:underline;">Przejdź do poradnika →</a>');
        }
      });
      wrap.appendChild(btn);
    });
    body.appendChild(wrap);
    scrollToBottom();
  }

  // ─── Quick Options ───
  const quickWrap = document.getElementById('VerdenQuickOptions');
  if (quickWrap) {
    quickWrap.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-intent]');
      if (!btn) return;
      const intent = btn.dataset.intent;
      addMessage(btn.textContent, 'user');
      if (intent === 'pick') Quiz.start();
      else if (intent === 'faq') say('Jasne! Pytaj o co chcesz — dostawę, zwroty, skład produktów, jak zacząć przygodę z zero waste... Jestem tu dla Ciebie. 💚');
      else if (intent === 'bestsellers') {
        const bestsellers = products.filter(p => p.available).slice(0, 3);
        showProducts(bestsellers, 'Oto produkty, które nasi klienci kochają najbardziej:');
      }
    });
  }

  // ─── Form Submit ───
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const text = input.value.trim();
    if (!text) return;
    addMessage(text, 'user');
    input.value = '';
    handleMessage(text);
  });

  // ─── Proactive Messages ───
  function showProactive(text) {
    if (panel.classList.contains('is-open')) return;
    const existing = document.querySelector('.vd-chat__proactive');
    if (existing) existing.remove();

    const bubble = document.createElement('div');
    bubble.className = 'vd-chat__proactive';
    bubble.innerHTML = `${text}<button class="vd-chat__proactive-close">×</button>`;
    document.body.appendChild(bubble);

    bubble.addEventListener('click', (e) => {
      if (e.target.classList.contains('vd-chat__proactive-close')) {
        bubble.remove();
        return;
      }
      bubble.remove();
      openPanel();
    });

    setTimeout(() => bubble.remove(), 15000);
  }

  // Proactive triggers
  setTimeout(() => {
    if (conversationTurns === 0 && !panel.classList.contains('is-open')) {
      showProactive('Hej! Widzę, że rozglądasz się po sklepie. Mogę pomóc dobrać idealny produkt? 🌿');
    }
  }, 25000);

  // Track product page time
  if (document.querySelector('.product-main')) {
    setTimeout(() => {
      if (conversationTurns === 0 && !panel.classList.contains('is-open')) {
        showProactive('Masz wątpliwości co do tego produktu? Mogę odpowiedzieć na pytania o skład, dostępność lub zastosowanie! 💚');
      }
    }, 20000);
  }

  // ─── Welcome on first open ───
  let hasWelcomed = false;
  toggle.addEventListener('click', () => {
    if (!hasWelcomed && !panel.classList.contains('is-open')) {
      hasWelcomed = true;
      setTimeout(() => {
        if (body.children.length <= 2) {
          say(pick(Verden.voice.greetings));
        }
      }, 300);
    }
  });

  // ─── Track viewed products from page ───
  const prodTitle = document.querySelector('.product-info__title');
  if (prodTitle) {
    const found = findProduct(prodTitle.textContent);
    if (found) {
      lastProduct = found;
      context.viewedProducts.push(found.id || found.title);
      saveContext();
    }
  }

})();
